import { t } from 'i18next';
import { Color, Line3, Points, Vector3 } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { isDefined } from '../../../../../../../utils/variables';
import cameraManager from '../../../../../../camera-manager';
import { CoordinateManager } from '../../../../../../coordinate-manager';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import {
  findCrossedLineSegmentIdFromIntersection,
  findLineSegmentIdFromIntersection,
  LineSegmentId,
} from '../../../../../../elements/design/design-node/utils/line-segment-node';
import {
  findPointIdFromIntersection,
  PointId,
} from '../../../../../../elements/design/design-node/utils/point-node';
import { MouseInputData } from '../../../../../../utilities/input-event/input-event-data-extractor';
import { ActionName, DesignTool } from '../../../../../design-tool';
import { PickingLine } from '../../../../../picking-line';
import { PickingSphere } from '../../../../../picking-sphere';
import { addAndCommitAction } from '../../utils/actions';
import { createNewPoint, setToolEditIntersectionToPoint } from '../../utils/point';
import { abortActionInProgress, initActionInProgress } from '../general';

const definedAndEqual = <T extends LineSegmentId | PointId | undefined>(a: T, b: T) =>
  isDefined(a) && a === b;

/**
 * Distance treshold, if the start position is closer than this distance
 * we will use the existing geometry.
 */
const distanceTreshold = 0.001;

const isOnLine = (line: Line3, point: Vector3) =>
  line.closestPointToPoint(point, true, new Vector3()).distanceTo(point) < distanceTreshold;

/**
 * Points can be placed while creating a line, which may split the line segment the line starts on.
 * In that case the start is resolved to the new line segment at the start position that is part of
 * the original line segment. Other line segments at the start position are never used.
 */
const resolveStart = (
  tool: DesignTool,
  startPoint: Vector3,
  initialLineSegmentIds: Set<LineSegmentId>,
  startLine?: Line3,
  pointId?: PointId,
  lineSegmentId?: LineSegmentId,
): { pointId?: PointId; lineSegmentId?: LineSegmentId } => {
  const node = tool.asset.designNode;

  if (!isDefined(lineSegmentId) || node.lineSegments.includes(lineSegmentId)) {
    return { pointId, lineSegmentId };
  }

  if (!isDefined(startLine)) {
    return {};
  }

  const localStart = node.getLocalPosition(startPoint);

  return {
    lineSegmentId: node.lineSegments.find((id) => {
      if (initialLineSegmentIds.has(id)) {
        return false;
      }

      const [a, b] = node.getVerticesByLineSegmentId(id, 'local');
      return (
        isOnLine(startLine, a) && isOnLine(startLine, b) && isOnLine(new Line3(a, b), localStart)
      );
    }),
  };
};

export const createLine = (tool: DesignTool): ContextMenuItem => {
  const startPoint = tool.currentPoint?.clone();

  const startPointId = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);
  const startLineSegmentId = findLineSegmentIdFromIntersection(
    tool.asset.designNode,
    tool.intersection,
  );
  const startCrossedLineSegmentId = findCrossedLineSegmentIdFromIntersection(
    tool.asset.designNode,
    tool.intersection,
  );

  const disabled = !isDefined(startPoint);

  return {
    title: t('contextMenu.create.items.line', { ns: 'skyviewDesign' }),
    action: () => {
      if (disabled) {
        return;
      }

      const initialLineSegmentIds = new Set(tool.asset.designNode.lineSegments);
      const startLine = isDefined(startLineSegmentId)
        ? new Line3(
            ...tool.asset.designNode.getVerticesByLineSegmentId(startLineSegmentId, 'local'),
          )
        : undefined;

      const startPointCursor = new PickingSphere(new Color(0.75, 0.75, 0.75));
      startPointCursor.position.copy(startPoint);

      const predictLineCursor = new PickingLine();
      predictLineCursor.updateVertex(0, startPoint);
      predictLineCursor.updateVertex(1, startPoint);

      initActionInProgress(tool, {
        actionName: ActionName.CreateLine,
        origin: startPoint,
        originLineSegmentId: startLineSegmentId,
        originPointId: startPointId,
        originCrossedLineSegmentId: startCrossedLineSegmentId,
        // A line starting on a line of another asset has its angles measured from that line.
        originAssetLine: tool.intersection ? undefined : tool.assetLine,
        initiate: () => {
          tool.add(startPointCursor);
          tool.add(predictLineCursor);
          tool.onEdited?.();
        },
        cleanup: () => {
          tool.remove(startPointCursor);
          tool.remove(predictLineCursor);
        },
        refresh: () => {
          predictLineCursor.updateVertex(0, startPointCursor.position);
          predictLineCursor.updateVertex(1, tool.currentPoint ?? startPointCursor.position);
        },
        onProjectProjectionUpdated(origoDiff) {
          startPointCursor.position.add(origoDiff);
        },
        readyToApply: () => isDefined(tool.currentPoint),
        getExtraOptions: (newEvent) =>
          startPoint ? [reSnapPoint(tool, startPoint, newEvent)] : [],
        apply: () => {
          let changes: RevertChange[] = [];
          const start = resolveStart(
            tool,
            startPoint,
            initialLineSegmentIds,
            startLine,
            startPointId,
            startLineSegmentId,
          );
          const endPointId = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);
          const endLineSegmentId = findLineSegmentIdFromIntersection(
            tool.asset.designNode,
            tool.intersection,
          );
          const endCrossedLineSegmentId = findCrossedLineSegmentIdFromIntersection(
            tool.asset.designNode,
            tool.intersection,
          );

          const endPoint = tool.currentPoint?.clone();

          if (!isDefined(endPoint)) {
            return;
          }

          if (
            definedAndEqual(start.pointId, endPointId) ||
            definedAndEqual(start.lineSegmentId, endLineSegmentId)
          ) {
            return;
          }

          const startCoordinate = CoordinateManager.getInstance().localToProjectCoords(startPoint);
          const endCoordinate = CoordinateManager.getInstance().localToProjectCoords(endPoint);
          const activeAttributes = tool.getActiveAttributes();

          addAndCommitAction(tool, {
            commit: () => {
              const result1 = createNewPoint(
                tool,
                startCoordinate,
                start.lineSegmentId,
                start.pointId,
                activeAttributes,
                startCrossedLineSegmentId,
              );
              const result2 = createNewPoint(
                tool,
                endCoordinate,
                endLineSegmentId,
                endPointId,
                activeAttributes,
                endCrossedLineSegmentId,
              );

              changes = [...result2.revertChanges, ...result1.revertChanges];

              // Create a new line segment between start and end point only if it does not already exist.
              if (
                !isDefined(tool.asset.designNode.findLineSegmentByPointIds(result1.id, result2.id))
              ) {
                changes.unshift(
                  tool.asset.designNode.addLineSegment(result1.id, result2.id, activeAttributes),
                );
              }

              tool.asset.designNode.refresh();
              tool.asset.refreshMetadata();

              // Manually set the snapped object to the newly created point
              setToolEditIntersectionToPoint(tool, result2.id);
            },
            rollback: () => {
              tool.asset.designNode.revert(changes);
              tool.asset.designNode.refresh();
              tool.asset.refreshMetadata();
            },
          });
        },
      });

      tool.contextMenuCallback?.();
    },
    disabled,
  };
};

const reSnapPoint = (tool: DesignTool, originPoint: Vector3, e: MouseInputData) => {
  const oldPosition = tool.intersection?.point!;
  const intersectedPointId = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);
  const disabled =
    !isDefined(intersectedPointId) ||
    !isDefined(oldPosition) ||
    !(tool.intersection?.object instanceof Points);

  return {
    title: t('contextMenu.resnapPoint', { ns: 'skyviewDesign' }),
    action: async () => {
      if (disabled) {
        return;
      }

      let newPoint = tool.findSnapPoint(e, cameraManager().getActiveCamera()!, false, oldPosition);

      if (isDefined(newPoint)) {
        newPoint = tool.asset.designNode.getLocalPosition(newPoint);
      }

      let changes: RevertChange[] = [];

      addAndCommitAction(tool, {
        commit: () => {
          changes = [];

          if (disabled) {
            return;
          }

          if (newPoint) {
            changes = [
              tool.asset.designNode.upsertPoint({
                id: intersectedPointId,
                vertex: newPoint,
              }),
            ];
          }

          if (tool.actionInProgress) {
            void abortActionInProgress(tool).action();
          }

          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
        rollback: () => {
          tool.asset.designNode.revert(changes);
          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
      });

      return Promise.resolve();
    },
    disabled,
  };
};
